// Broadnet → Productos y Servicios: catálogo de recargas/servicios básicos con
// montos, comisión, reversa/consulta y los "campos requeridos" que el POS debe
// capturar al vender (teléfono, N° de contrato, etc.). Punto clave de configurabilidad.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Coins, Loader2, Package, Palette, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import {
  BnProductoFull, CampoRequeridoBn, Campo, DESTINOS_CAMPO_BN, Modal, Switch, TIPOS_CAMPO_BN, TipoCampoBn,
  bnCatalogoSeed, bnProductoEliminar, bnProductoGuardar, bnProductosListar, fieldCls, money,
  BnMarca, bnCatalogoVisualSeed, bnMarcasListar, bnProductoMarca,
} from './shared';
import { MarcaLogo } from './CatalogoBroadnet';
import { MarcasBroadnet, MontosBroadnet } from './CatalogoBroadnetAdmin';

type Tab = 'recarga' | 'servicio_basico';

const vacio = {
  id: '', proveedor_codigo: '', proveedor_nombre: '', servicio_codigo: '', servicio_nombre: '',
  tipo: 'recarga' as Tab, tran_codigo: '', soporta_reversa: false, soporta_consulta: false,
  monto_modo: 'abierto' as 'fijo' | 'abierto' | 'rango', monto_fijo: '', monto_min: '', monto_max: '',
  comision_pct: '', comision_valor: '', orden: '0', es_activo: true,
  gestion_modo: 'heredar' as 'heredar' | 'ninguna' | 'fija' | 'rangos',
  gestion_valor: '', gestion_iva_pct: '',
  campos_requeridos: [] as CampoRequeridoBn[],
};

export function ProductosBroadnet() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const [tab, setTab] = useState<Tab>('recarga');
  const { data: productos = [], isFetching } = useQuery<BnProductoFull[]>({
    queryKey: ['pos-bn-productos', tab], queryFn: () => bnProductosListar(tab), staleTime: 0,
  });

  // 911: catálogo visual — marcas y montos
  const { data: marcas = [] } = useQuery<BnMarca[]>({ queryKey: ['pos-bn-marcas'], queryFn: bnMarcasListar, staleTime: 0 });
  const [verMarcas, setVerMarcas] = useState(false);
  const [montosDe, setMontosDe] = useState<string | null>(null);
  const cambiarMarca = useMutation({
    mutationFn: (v: { id: string; marca: string | null }) => bnProductoMarca(v.id, v.marca),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['pos-bn-productos'] }); void qc.invalidateQueries({ queryKey: ['pos-bn-marcas'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const seed = useMutation({
    // Lo importado entra sin marca: se le asigna la de su familia y los montos típicos.
    mutationFn: async () => { const r = await bnCatalogoSeed(); await bnCatalogoVisualSeed(); return r; },
    onSuccess: (r) => { toast.success(`Catálogo importado: ${r.recargas} recargas, ${r.servicios_basicos} servicios básicos (nuevos)`); void qc.invalidateQueries({ queryKey: ['pos-bn-productos'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const toggleActivo = useMutation({
    mutationFn: (p: BnProductoFull) => bnProductoGuardar({ id: p.id, es_activo: !p.es_activo }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['pos-bn-productos'] }),
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  // Cambiar quién factura es un clic, sin abrir el formulario: es lo que se
  // toca una vez por operadora y no se vuelve a mirar.
  const facturacion = useMutation({
    mutationFn: (v: { id: string; valor: string }) => bnProductoGuardar({ id: v.id, facturacion: v.valor }),
    onSuccess: () => {
      toast.success('Facturación actualizada');
      void qc.invalidateQueries({ queryKey: ['pos-bn-productos'] });
      void qc.invalidateQueries({ queryKey: ['pos-bn-config-recarga'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminar = useMutation({
    mutationFn: (id: string) => bnProductoEliminar(id),
    onSuccess: () => { toast.success('Producto desactivado'); void qc.invalidateQueries({ queryKey: ['pos-bn-productos'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<typeof vacio>(vacio);
  const [abierto, setAbierto] = useState(false);
  const set = (patch: Partial<typeof vacio>) => setForm((f) => ({ ...f, ...patch }));

  const abrir = (p?: BnProductoFull) => {
    if (p) {
      setEditId(p.id);
      setForm({
        id: p.id, proveedor_codigo: p.proveedor_codigo, proveedor_nombre: p.proveedor_nombre,
        servicio_codigo: p.servicio_codigo, servicio_nombre: p.servicio_nombre, tipo: p.tipo,
        tran_codigo: p.tran_codigo ?? '', soporta_reversa: p.soporta_reversa, soporta_consulta: p.soporta_consulta,
        monto_modo: p.monto_modo, monto_fijo: p.monto_fijo != null ? String(p.monto_fijo) : '',
        monto_min: p.monto_min != null ? String(p.monto_min) : '', monto_max: p.monto_max != null ? String(p.monto_max) : '',
        comision_pct: p.comision_pct != null ? String(p.comision_pct) : '', comision_valor: p.comision_valor != null ? String(p.comision_valor) : '',
        gestion_modo: p.gestion_modo ?? 'heredar',
        gestion_valor: p.gestion_valor != null ? String(p.gestion_valor) : '',
        gestion_iva_pct: p.gestion_iva_pct != null ? String(p.gestion_iva_pct) : '',
        orden: String(p.orden), es_activo: p.es_activo, campos_requeridos: p.campos_requeridos ?? [],
      });
    } else {
      setEditId(null); setForm({ ...vacio, tipo: tab });
    }
    setAbierto(true);
  };

  const guardar = useMutation({
    mutationFn: () => bnProductoGuardar({
      id: editId || undefined,
      proveedor_codigo: form.proveedor_codigo.trim(), proveedor_nombre: form.proveedor_nombre.trim(),
      servicio_codigo: form.servicio_codigo.trim() || undefined, servicio_nombre: form.servicio_nombre.trim() || undefined,
      tipo: form.tipo, tran_codigo: form.tran_codigo || null,
      soporta_reversa: form.soporta_reversa, soporta_consulta: form.soporta_consulta,
      monto_modo: form.monto_modo,
      monto_fijo: form.monto_modo === 'fijo' ? (form.monto_fijo || null) : null,
      monto_min: form.monto_modo === 'rango' ? (form.monto_min || null) : null,
      monto_max: form.monto_modo === 'rango' ? (form.monto_max || null) : null,
      comision_pct: form.comision_pct || null, comision_valor: form.comision_valor || null,
      gestion_modo: form.gestion_modo,
      gestion_valor: form.gestion_valor || null,
      gestion_iva_pct: form.gestion_iva_pct || null,
      campos_requeridos: form.campos_requeridos, orden: Number(form.orden) || 0, es_activo: form.es_activo,
    }),
    onSuccess: () => { toast.success('Producto guardado'); setAbierto(false); void qc.invalidateQueries({ queryKey: ['pos-bn-productos'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const agregarCampo = () => set({ campos_requeridos: [...form.campos_requeridos, { clave: '', label: '', tipo: 'texto', requerido: true }] });
  const editarCampo = (i: number, patch: Partial<CampoRequeridoBn>) =>
    set({ campos_requeridos: form.campos_requeridos.map((c, idx) => (idx === i ? { ...c, ...patch } : c)) });
  const quitarCampo = (i: number) => set({ campos_requeridos: form.campos_requeridos.filter((_, idx) => idx !== i) });

  // Filtro por marca: solo las marcas que tienen productos en esta pestaña, con su cantidad.
  const [fMarca, setFMarca] = useState('');   // '' = todas · '_sin' = sin marca · id
  const marcasTab = useMemo(() => {
    const m = new Map<string, { id: string; nombre: string; n: number }>();
    for (const p of productos) {
      const k = p.marca_id ?? '_sin';
      const nombre = p.marca?.nombre ?? '— Sin marca —';
      m.set(k, { id: k, nombre, n: (m.get(k)?.n ?? 0) + 1 });
    }
    return [...m.values()].sort((a, b) => (a.id === '_sin' ? 1 : b.id === '_sin' ? -1 : a.nombre.localeCompare(b.nombre, 'es')));
  }, [productos]);
  const filtrados = useMemo(() => (fMarca ? productos.filter((p) => (p.marca_id ?? '_sin') === fMarca) : productos), [productos, fMarca]);
  const totalActivos = useMemo(() => filtrados.filter((p) => p.es_activo).length, [filtrados]);

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-xl font-semibold flex items-center gap-2"><Package className="w-5 h-5" /> Broadnet · Productos y Servicios</h1>
        <div className="flex gap-2">
          <Button variant="outline" isLoading={seed.isPending}
            onClick={async () => { if (await dlg.confirmar('¿Importar el catálogo del manual?', { detalle: 'Catálogo estándar del manual, desactivado por defecto; no duplica lo existente.' })) seed.mutate(); }}>
            Importar catálogo del manual
          </Button>
          <Button variant="outline" onClick={() => setVerMarcas(true)}><Palette className="w-4 h-4 mr-1" /> Marcas</Button>
          <Button onClick={() => abrir()}><Plus className="w-4 h-4 mr-1" /> Nuevo</Button>
        </div>
      </div>

      <div className="flex items-center gap-2 border-b">
        {([['recarga', 'Recargas'], ['servicio_basico', 'Servicios básicos']] as const).map(([k, l]) => (
          <button key={k} onClick={() => { setTab(k); setFMarca(''); }}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === k ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
            {l}
          </button>
        ))}
      </div>

      <Card>
        <CardHeader className="pb-2 flex flex-row flex-wrap items-center justify-between gap-2 space-y-0">
          <CardTitle className="text-base">
            {tab === 'recarga' ? 'Recargas' : 'Servicios básicos'} ({fMarca ? `${filtrados.length} de ${productos.length}` : productos.length}) · activos {totalActivos}
            {isFetching && <Loader2 className="inline w-4 h-4 animate-spin ml-1" />}
          </CardTitle>
          <label className="flex items-center gap-2 text-xs text-muted-foreground">Marca
            <select className={`${fieldCls} h-8 w-48 text-sm`} value={fMarca} onChange={(e) => setFMarca(e.target.value)}>
              <option value="">Todas</option>
              {marcasTab.map((m) => <option key={m.id} value={m.id}>{m.nombre} ({m.n})</option>)}
            </select>
            {fMarca && <button className="text-primary hover:underline" onClick={() => setFMarca('')}>Limpiar</button>}
          </label>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3 text-right">N°</th><th className="py-2 pr-3">Cód.</th><th className="py-2 pr-3">Marca</th><th className="py-2 pr-3">Proveedor</th>
              <th className="py-2 pr-3">Servicio</th><th className="py-2 pr-3">Monto</th>{tab === 'recarga' && <th className="py-2 pr-3">Tarjetas</th>}<th className="py-2 pr-3 text-right">Comisión</th>
              <th className="py-2 pr-3">Regularización</th><th className="py-2 pr-3">Campos</th><th className="py-2 pr-3">¿Quién factura?</th><th className="py-2 pr-3">Activo</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {filtrados.length === 0 && (
                <tr><td colSpan={tab === 'recarga' ? 13 : 12} className="py-8 text-center text-muted-foreground">
                  {fMarca ? 'Esta marca no tiene productos en esta pestaña'
                    : <>Sin productos en esta pestaña — usa &quot;Importar catálogo del manual&quot; o &quot;+ Nuevo&quot;</>}
                </td></tr>
              )}
              {filtrados.map((p, i) => (
                <tr key={p.id} className="border-b last:border-0 hover:bg-muted/30">
                  <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                  <td className="py-2 pr-3 font-mono text-xs">{p.proveedor_codigo}/{p.servicio_codigo}</td>
                  <td className="py-2 pr-3">
                    <div className="flex items-center gap-1.5">
                      {p.marca && <MarcaLogo marca={p.marca} size={22} />}
                      <select className={`${fieldCls} h-8 w-32 text-xs`} value={p.marca_id ?? ''}
                        onChange={(e) => cambiarMarca.mutate({ id: p.id, marca: e.target.value || null })}>
                        <option value="">— Sin marca —</option>
                        {marcas.map((m) => <option key={m.id} value={m.id}>{m.nombre}</option>)}
                      </select>
                    </div>
                  </td>
                  <td className="py-2 pr-3">{p.proveedor_nombre}</td>
                  <td className="py-2 pr-3 text-xs text-muted-foreground">{p.servicio_nombre}</td>
                  <td className="py-2 pr-3 text-xs">
                    {p.monto_modo === 'fijo' && p.monto_fijo != null ? money(p.monto_fijo) : p.monto_modo === 'rango' ? `${money(p.monto_min ?? 0)} – ${money(p.monto_max ?? 0)}` : 'Abierto'}
                  </td>
                  {tab === 'recarga' && (
                    <td className="py-2 pr-3">
                      <button className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs hover:bg-muted" title="Montos/paquetes que aparecen como tarjetas en el F4"
                        onClick={() => setMontosDe(p.id)}>
                        <Coins className="h-3.5 w-3.5" /> {(p.denominaciones ?? []).filter((d) => d.es_activo).length || 'Agregar'}
                      </button>
                    </td>
                  )}
                  <td className="py-2 pr-3 text-right font-mono text-xs">{p.comision_pct != null ? `${p.comision_pct}%` : p.comision_valor != null ? money(p.comision_valor) : '—'}</td>
                  <td className="py-2 pr-3 text-xs">
                    {p.soporta_reversa && <span className="rounded-full px-2 py-0.5 bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 mr-1" title="Soporta anulación: ante falla se envía REVERSO (tipo 11) y no se factura">Reversa</span>}
                    {p.soporta_consulta && <span className="rounded-full px-2 py-0.5 bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300" title="Sin anulación: ante falla se envía CONFIRMACIÓN (tipo 12) con la referencia en el campo autorización">Confirmación</span>}
                    {!p.soporta_reversa && !p.soporta_consulta && '—'}
                  </td>
                  <td className="py-2 pr-3 text-xs text-muted-foreground">{p.campos_requeridos?.length ?? 0}</td>
                  {/* «Heredar» no significa lo mismo para los dos tipos: una recarga sigue
                      el ajuste de la empresa, un servicio básico va siempre con recibo
                      porque una recaudación no es una venta. Por eso se muestra al lado
                      lo que va a pasar de verdad. */}
                  <td className="py-2 pr-3">
                    <select className={`${fieldCls} h-8 text-xs w-40`} value={p.facturacion ?? 'heredar'}
                      onChange={(e) => facturacion.mutate({ id: p.id, valor: e.target.value })}>
                      <option value="heredar">Heredar</option>
                      <option value="comercio">El comercio (factura SRI)</option>
                      <option value="broadnet">Broadnet (recibo)</option>
                    </select>
                    {(p.facturacion ?? 'heredar') === 'heredar' && (
                      <div className="text-[11px] text-muted-foreground mt-0.5">
                        → {p.facturacion_efectiva === 'comercio' ? 'factura el comercio' : 'recibo'}
                      </div>
                    )}
                  </td>
                  <td className="py-2 pr-3"><Switch checked={p.es_activo} onChange={() => toggleActivo.mutate(p)} label="" /></td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" onClick={() => abrir(p)}><Pencil className="w-4 h-4" /></button>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 ml-1" title="Desactivar"
                      onClick={async () => { if (await dlg.confirmar(`¿Desactivar "${p.proveedor_nombre}"?`, { peligro: true })) eliminar.mutate(p.id); }}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {verMarcas && <MarcasBroadnet onClose={() => setVerMarcas(false)} />}
      {montosDe && productos.find((x) => x.id === montosDe) && (
        <MontosBroadnet producto={productos.find((x) => x.id === montosDe)!} onClose={() => setMontosDe(null)} />
      )}

      {abierto && (
        <Modal title={editId ? 'Editar producto' : 'Nuevo producto'} onClose={() => setAbierto(false)}>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Tipo">
                <select className={fieldCls} value={form.tipo} onChange={(e) => set({ tipo: e.target.value as Tab })}>
                  <option value="recarga">Recarga</option>
                  <option value="servicio_basico">Servicio básico</option>
                </select>
              </Campo>
              <Campo label="Código de transacción Broadnet (02/99/15…)">
                <input className={fieldCls} value={form.tran_codigo} onChange={(e) => set({ tran_codigo: e.target.value })} />
              </Campo>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Código proveedor *"><input className={fieldCls} value={form.proveedor_codigo} onChange={(e) => set({ proveedor_codigo: e.target.value })} /></Campo>
              <Campo label="Nombre proveedor *"><input className={fieldCls} value={form.proveedor_nombre} onChange={(e) => set({ proveedor_nombre: e.target.value })} /></Campo>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Código servicio (= proveedor si vacío)"><input className={fieldCls} value={form.servicio_codigo} onChange={(e) => set({ servicio_codigo: e.target.value })} /></Campo>
              <Campo label="Nombre servicio (= proveedor si vacío)"><input className={fieldCls} value={form.servicio_nombre} onChange={(e) => set({ servicio_nombre: e.target.value })} /></Campo>
            </div>

            <div className="flex items-center gap-4 border-t pt-3">
              <Switch checked={form.soporta_reversa} onChange={(v) => set({ soporta_reversa: v })} label="Soporta reversa" />
              <Switch checked={form.soporta_consulta} onChange={(v) => set({ soporta_consulta: v })} label="Soporta consulta/confirmación" />
            </div>

            <div className="border-t pt-3 space-y-3">
              <Campo label="Modo de monto">
                <select className={fieldCls} value={form.monto_modo} onChange={(e) => set({ monto_modo: e.target.value as typeof form.monto_modo })}>
                  <option value="abierto">Abierto (el cajero digita el monto)</option>
                  <option value="fijo">Fijo (monto único)</option>
                  <option value="rango">Rango (mínimo–máximo)</option>
                </select>
              </Campo>
              {form.monto_modo === 'fijo' && (
                <Campo label="Monto fijo"><input type="number" min={0} step="0.01" className={fieldCls} value={form.monto_fijo} onChange={(e) => set({ monto_fijo: e.target.value })} /></Campo>
              )}
              {form.monto_modo === 'rango' && (
                <div className="grid grid-cols-2 gap-3">
                  <Campo label="Monto mínimo"><input type="number" min={0} step="0.01" className={fieldCls} value={form.monto_min} onChange={(e) => set({ monto_min: e.target.value })} /></Campo>
                  <Campo label="Monto máximo"><input type="number" min={0} step="0.01" className={fieldCls} value={form.monto_max} onChange={(e) => set({ monto_max: e.target.value })} /></Campo>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3 border-t pt-3">
              <Campo label="Comisión (%)"><input type="number" min={0} step="0.001" className={fieldCls} value={form.comision_pct} onChange={(e) => set({ comision_pct: e.target.value })} /></Campo>
              <Campo label="Comisión fija ($)"><input type="number" min={0} step="0.0001" className={fieldCls} value={form.comision_valor} onChange={(e) => set({ comision_valor: e.target.value })} /></Campo>
              {/* Comisión de GESTIÓN: la que este producto le cobra al CLIENTE.
                  Las dos de arriba son lo que Broadnet le paga al comercio. */}
              <Campo label="Gestión al cliente">
                <select className={fieldCls} value={form.gestion_modo}
                  onChange={(e) => set({ gestion_modo: e.target.value as typeof form.gestion_modo })}>
                  <option value="heredar">Como diga la empresa</option>
                  <option value="ninguna">No cobrar</option>
                  <option value="fija">Valor fijo</option>
                  <option value="rangos">Por rango de monto</option>
                </select>
              </Campo>
              <Campo label="Gestión ($)">
                <input type="number" min={0} step="0.01" className={fieldCls} disabled={form.gestion_modo !== 'fija'}
                  value={form.gestion_valor} onChange={(e) => set({ gestion_valor: e.target.value })} />
              </Campo>
              <Campo label="IVA gestión (%)">
                <input type="number" min={0} step="0.01" className={fieldCls} disabled={form.gestion_modo === 'heredar' || form.gestion_modo === 'ninguna'}
                  value={form.gestion_iva_pct} onChange={(e) => set({ gestion_iva_pct: e.target.value })} />
              </Campo>
            </div>

            <div className="border-t pt-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Campos requeridos (lo que el cajero debe capturar)</span>
                <Button size="sm" variant="outline" onClick={agregarCampo}><Plus className="w-3.5 h-3.5 mr-1" /> Campo</Button>
              </div>
              {form.campos_requeridos.length === 0 && <p className="text-xs text-muted-foreground">Sin campos adicionales (solo monto).</p>}
              {form.campos_requeridos.map((c, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-3">
                    <Campo label="Clave"><input className={`${fieldCls} h-9`} value={c.clave} onChange={(e) => editarCampo(i, { clave: e.target.value })} placeholder="telefono" /></Campo>
                  </div>
                  <div className="col-span-4">
                    <Campo label="Etiqueta"><input className={`${fieldCls} h-9`} value={c.label} onChange={(e) => editarCampo(i, { label: e.target.value })} placeholder="Teléfono / número" /></Campo>
                  </div>
                  <div className="col-span-3">
                    <Campo label="Tipo">
                      <select className={`${fieldCls} h-9`} value={c.tipo} onChange={(e) => editarCampo(i, { tipo: e.target.value as TipoCampoBn })}>
                        {TIPOS_CAMPO_BN.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
                      </select>
                    </Campo>
                  </div>
                  <div className="col-span-1 flex items-center justify-center pb-2">
                    <input type="checkbox" title="Requerido" checked={c.requerido} onChange={(e) => editarCampo(i, { requerido: e.target.checked })} className="h-4 w-4 rounded border-input" />
                  </div>
                  <div className="col-span-1 flex justify-end pb-1">
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20" onClick={() => quitarCampo(i)}><Trash2 className="w-4 h-4" /></button>
                  </div>
                  <div className="col-span-5">
                    <Campo label="Destino en la trama (vacío = cuenta/celular)">
                      <select className={`${fieldCls} h-9`} value={c.destino ?? ''} onChange={(e) => editarCampo(i, { destino: e.target.value || undefined })}>
                        <option value="">cuenta / celular</option>
                        {DESTINOS_CAMPO_BN.map((d) => <option key={d} value={d}>{d}</option>)}
                      </select>
                    </Campo>
                  </div>
                  {c.tipo === 'combo' && (
                    <div className="col-span-7">
                      <Campo label="Opciones (una por línea: valor|etiqueta)">
                        <textarea className={`${fieldCls} !h-20 font-mono text-xs`} rows={3}
                          value={(c.opciones ?? []).map((o) => `${o.valor}|${o.label}`).join('\n')}
                          onChange={(e) => editarCampo(i, { opciones: e.target.value.split('\n').map((l) => l.trim()).filter(Boolean)
                            .map((l) => { const [valor, ...r] = l.split('|'); return { valor: valor.trim(), label: r.join('|').trim() || valor.trim() }; }) })} />
                      </Campo>
                    </div>
                  )}
                  <div className="hidden">
                  </div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-3 border-t pt-3">
              <Campo label="Orden"><input type="number" className={fieldCls} value={form.orden} onChange={(e) => set({ orden: e.target.value })} /></Campo>
              <div className="flex items-end pb-2"><Switch checked={form.es_activo} onChange={(v) => set({ es_activo: v })} label="Activo" /></div>
            </div>

            <div className="flex justify-end gap-2 border-t pt-3">
              <Button variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
              <Button isLoading={guardar.isPending} disabled={!form.proveedor_codigo.trim() || !form.proveedor_nombre.trim()} onClick={() => guardar.mutate()}>Guardar</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
